<template>
  <v-row>
    <v-dialog
      v-model="showDialog"
      scrollable
      max-width="1150px"
      max-height="90%"
      :retain-focus="false"
    >
      <v-skeleton-loader
        v-if="loadingSkeleton"
        type="article, actions"
      ></v-skeleton-loader>
      <v-card v-if="!loadingSkeleton">
        <v-card-title>
          <v-panel>
            <span style="font-size: 20px;">
              {{
                !data || !data.id
                  ? $t("wkt.admin.profiles.edit-window.new_profile_title")
                  : $t("wkt.admin.profiles.edit-window.edit_profile_title")
              }}
            </span>
          </v-panel>
        </v-card-title>
        <v-card-text @wheel="onWheel">
          <v-container fluid>
            <v-tabs v-model="tab" centered grow>
              <v-tab>{{ $t("wkt.admin.profiles.window.tab-data-access.title").toUpperCase() }}</v-tab>
              <v-tab>{{ $t("wkt.admin.profiles.window.tab-access-modules.title").toUpperCase() }}</v-tab>
              <v-tab v-if="sessionUserIsSuperAdmin">{{ $t("wkt.admin.profiles.window.tab-super-administrators.title").toUpperCase() }}</v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab">
              <v-tab-item eager>
                <v-form ref="form1">
                  <v-row class="mt-4">
                    <v-col cols="4">
                      <v-text-field
                        :value="formatLongDateTime(data.creationDate)"
                        :label="$t('wkt.admin.profiles.edit-window.created')"
                        disabled
                        autocomplete="off"
                      />
                    </v-col>
                    <v-col cols="4">
                      <v-text-field
                        v-model="data.employeeEmail"
                        :label="$t('wkt.admin.profiles.edit-window.email')"
                        :rules="[validationRules.required]"
                        :disabled="isEditing"
                        autocomplete="off"
                        @blur="onBlurEvent"
                      />
                    </v-col>
                    <v-col cols="4" v-if="isCompanySelectorVisible">
                      <v-select
                        v-model="data.employeeCompanyId"
                        :items="companyOptions"
                        item-value="id"
                        item-text="name"
                         @change="onCompanySelected"
                        :label="$t('wkt.admin.profiles.edit-window.company')"
                        :rules="companySelectorRules"
                      />
                    </v-col>
                    <v-col cols="4" v-if="isEditing && !isCompanySelectorVisible">
                      <v-text-field
                        :value="data.employeeCompanyName"
                        :label="$t('wkt.admin.profiles.edit-window.company')"
                        disabled
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <span style="font-size:18px; font-weight:500; color:black">
                        {{ $t('wkt.admin.profiles.edit-window.admin_enabled.title') }}
                      </span>
                    </v-col>
                  </v-row>
                  <v-row style="display:flex; align-items:center; margin-left:1px">
                    <v-col cols="12">
                      <v-switch :label="$t('wkt.admin.profiles.edit-window.enabled')" v-model="data.enabled" :disabled="isReadOnly" />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <span style="font-size:18px; font-weight:500; color:black">
                        {{ $t('wkt.admin.profiles.edit-window.admin_access_restriction.title') }}
                      </span>
                    </v-col>
                    <v-col cols="12">
                      <span>
                        {{ $t('wkt.admin.profiles.edit-window.admin_access_restriction.description') }}
                      </span>
                    </v-col>
                    <v-col cols="12">
                      <v-autocomplete
                        v-model="data.workplaces"
                        :items="workplaces"
                        item-text="name"
                        item-value="id"
                        attach
                        :label="$t('wkt.admin.profiles.edit-window.allow_workplaces')"
                        chips
                        multiple
                        clearable
                        deletable-chips
                        :disabled="isReadOnly"
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-autocomplete
                        v-model="data.departments"
                        :items="departments"
                        item-text="name"
                        item-value="id"
                        attach
                        :label="$t('wkt.admin.profiles.edit-window.allow_departments')"
                        chips
                        multiple
                        clearable
                        deletable-chips
                        :disabled="isReadOnly"
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-autocomplete
                        v-model="data.projects"
                        :items="projects"
                        item-text="shortName"
                        item-value="id"
                        attach
                        :label="$t('wkt.admin.profiles.edit-window.allow_projects')"
                        chips
                        multiple
                        clearable
                        deletable-chips
                        :disabled="isReadOnly"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <span style="font-size:18px; font-weight:500; color:black">
                        {{ $t('wkt.admin.profiles.edit-window.admin_more_options.title') }}
                      </span>
                    </v-col>
                  </v-row>
                  <v-row style="display:flex; align-items:center; margin-left:1px">
                    <v-col cols="12">
                      <v-switch :label="$t('wkt.admin.profiles.edit-window.admin.only_my_team')" v-model="data.onlyMyTeam" :disabled="isReadOnly" />
                    </v-col>
                  </v-row>
                  <v-row style="display:flex; align-items:center; margin-left:1px; margin-top:-25px">
                    <v-col cols="12">
                      <v-switch :label="$t('wkt.admin.profiles.edit-window.receive_monthly_summary')" v-model="data.sendMonthlySummary" :disabled="isReadOnly" />
                    </v-col>
                  </v-row>
                </v-form>
              </v-tab-item>
              <!-- TAB 2 -->
              <v-tab-item eager>
                <v-form ref="form2">
                  <AdminAccessModuleTab :data.sync="data" :is-read-only="isReadOnly" :company="currentCompany" :i-am-partner="isPartner" :is-super-admin="sessionUserIsSuperAdmin" />
                </v-form>
              </v-tab-item>
              <!-- TAB 3 -->
              <v-tab-item eager v-if="sessionUserIsSuperAdmin">
                <v-form ref="form3">
                  <SuperAdminTab :data.sync="data" :is-read-only="isReadOnly" />
                </v-form>
              </v-tab-item>
            </v-tabs-items>
          </v-container>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn v-if="isEditing"
            color="#ff0000"
            @click="confirmDelete()"
            :disabled="isReadOnly">
            <v-icon left color="#ffffff">delete</v-icon>
            <span style="color: #ffffff;">{{
              $t("wkt.admin.common.dialog-buttons.delete").toUpperCase()
            }}</span>
          </v-btn>
          <v-spacer />
          <v-btn color="primary" text @click="onSave" :disabled="!canSave || isReadOnly">{{
            $t("wkt.admin.common.dialog-buttons.ok").toUpperCase()
          }}</v-btn>
          <v-btn text @click="showDialog = false">{{
            $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase()
          }}</v-btn>
        </v-card-actions>
      </v-card>
      <LoadingDialog :loading="loading" />
    </v-dialog>

    <ConfirmDialog
      ref='dlgConfirm'
      :title="$t('wkt.admin.profiles.confirm-dialog.title')"
      :message="$t('wkt.admin.profiles.confirm-window.text')"
      :titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      :titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="deleteItem()"
    />
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import api from "../../../api/worktime.js";
import gwt from "../../../api/gwt";
import AdminAccessModuleTab from "./AdminAccessModuleTab.vue";
import SuperAdminTab from './SuperAdminTab.vue';
import { Constants } from '../../common/Constants.js';
import ConfirmDialog from "../../common/ConfirmDialog.vue";
import LoadingDialog from '../../common/LoadingDialog.vue';

export default {
  name: "ProfilesDialog",
  mixins: [BaseModule],
  components: { AdminAccessModuleTab, SuperAdminTab, ConfirmDialog, LoadingDialog },

  data() {
    return {
      showDialog: false,
      loading: false,
      loadingSkeleton: false,
      tab: 0,
      data: {
        id: null,
        creationDate: null,
        employeeEmail: '',
        employeeCompanyName: null,
        employeeCompanyId: null,
        employeeId: null,
        companyId: null,
        enabled: true,
        workplaces: [],
        departments: [],
        projects: [],
        onlyMyTeam: false,
        sendMonthlySummary: true
      },
      workplaces: [], departments: [], projects: [],
      profile: null, sessionEmployee: null,
      fullEmployees: [], companyOptions: [],
      isCompanySelectorVisible: false,
      companyValid: false,
      employeesFetched: false,
      isPartner: false,
      checkingPartner: false,
      employeesFetching: false,
      pendingStoreAfter: false
    };
  },
  computed: {
    isEditing() {
      return !!this.data.id;
    },
    sessionModuleProfiles() {
      return this.profile?.moduleProfiles;
    },
    sessionUserIsSuperAdmin() {
      return this.sessionEmployee?.employee?.isSuperAdmin === true;
    },
    userHasAnyAccess() {
      const mp = this.sessionModuleProfiles;
      return !(mp === Constants.DENY_ALL && !this.sessionUserIsSuperAdmin);
    },
    isReadOnly() {
      return (
        this.sessionModuleProfiles === Constants.ALLOW_READ_ONLY &&
        !this.sessionUserIsSuperAdmin
      );
    },
    companySelectorRules() {
      return this.isCompanySelectorVisible
        ? [v => (v !== '' && v !== null && v !== undefined) || this.$t('wkt.admin.common.select_company')]
        : [];
    },
    canSave() {
      if (this.isEditing) return true;

      if (this.isCompanySelectorVisible) {
        return this.companyValid === true;
      }

      return true;
    },
    currentCompany() {
      return this.sessionEmployee?.company;
    }
  },
  watch: {
    'data.employeeEmail'() {
      this.employeesFetched = false;
      this.companyValid = false;
      this.isCompanySelectorVisible = false;
    }
  },
  methods: {
    onWheel(event) {
      event.stopPropagation();
    },
    open(action, payload) {
      if (!this.userHasAnyAccess) {
        this.$emit("show-alert", { text: this.$t("wkt.admin.profiles.no_permission"), color: "error", timeout: 3000 });
        return;
      }

      // reset de validaciones
      this.$refs.form1?.resetValidation();
      this.tab = 0;
      this.employeesFetched = false;
      this.hideCompanyDropDown();

      // Carga datos auxiliares
      this.sessionEmployee = gwt.getSessionInformation();
      this.profile = gwt.getSessionProfile();
      this.getWorkplaces();
      this.getDepartments();
      this.getProjects();

    this.checkingPartner = true;
    api.iAmPartner(
      gwt.getSessionInformation().company.id,
      (res) => {
        this.isPartner = !!res;
        this.checkingPartner = false;
      },
      (err) => {
        this.isPartner = false;
        this.checkingPartner = false;
        this.$log.error(err);
      }
    );

      this.showDialog = true;

      // Creación vs edición
      if (!payload) {
        this.initNewData();
        return;
      }

      this.loadingSkeleton = true;
      api.getFullProfileById(
        payload,
        (res) => {
          this.data = this.transformProfile(res);
          this.loadingSkeleton = false;
        },
        (err) => {
          this.loadingSkeleton = false;
          this.$log.error(err);
          this.$emit("show-alert", { text: err.message, color: "error", timeout: 3000 });
        }
      );
    },
    initNewData() {
      const isSA = this.sessionUserIsSuperAdmin; 
      // Campos de todos los módulos:
      const moduleFields = [
        // — Tab 2 (AdminAccessModuleTab) —
        "moduleHome","moduleStaff","moduleHolidays","moduleTeams","moduleHCalendar",
        "moduleAssessments","moduleMessages","moduleNews","moduleSurveys",
        "moduleDaily","moduleMonthly","moduleEmployee","moduleTelework",
        "moduleProjects","modulePlanning","moduleCalendar",
        "moduleProcedures","moduleProceduresCompleteRequests","moduleProceduresMassActions",
        "moduleDocuments","modulePDocuments","moduleDocumentsExternal","viewPrivateDocuments",
        "moduleComplaintConfig","moduleComplaint","moduleExpenseSheet","moduleExpenseSheetCategory",
        "moduleVPolicy","moduleWorkplace","moduleDepartments","modulePForms",
        "moduleDocumentCategory","moduleDocumentsCategoryExternal","modulePDefinition","moduleActivityType",
        "moduleChatConfig","moduleGConfig","moduleGCustomize","moduleIntegration","moduleProfiles",
        "modulePartnerCustomers","moduleAI","moduleLicense","modulePaidAbsencesCategory",
        // — Tab 3 (SuperAdminTab) —
        "moduleCustomers","modulePartners","moduleTransactions",
        "modulePurchases","modulePrices","moduleVouchers","moduleOnBoardMessages",
        "moduleWebhooks","moduleReportType","moduleKiosk","moduleAdminConfig"
      ];

      const base = {
        id: null,
        creationDate: Date.now(),
        employeeEmail: "",
        employeeCompanyName: null,
        enabled: true,
        workplaces: [],
        departments: [],
        projects: [],
        onlyMyTeam: false,
        sendMonthlySummary: true,
      };

      moduleFields.forEach(f => {
        if (!isSA && [
            // aquí solo el listado de campos superAdmin:
            "moduleCustomers","modulePartners","moduleTransactions",
            "modulePurchases","modulePrices","moduleVouchers","moduleOnBoardMessages",
            "moduleWebhooks","moduleReportType","moduleKiosk","moduleAdminConfig"
          ].includes(f)
        ) {
          base[f] = 0;
        } else {
          base[f] = 2;
        }
      });

      base.moduleHome = 2;
      base.viewPrivateDocuments = 1;
      base.moduleAI = 0;
      base.moduleComplaint = 0;
      base.moduleComplaintConfig = 0;
      base.moduleReportType = 0;

      this.data = base;
    },
    transformProfile(res) {
      return {
        ...res,
        workplaces: res.workplaces ? res.workplaces.split(',').map(Number) : [],
        departments: res.departments ? res.departments.split(',').map(Number) : [],
        projects: res.projects ? res.projects.split(',').map(Number) : []
      };
    },
    getWorkplaces() {
      this.loadingSkeleton = true;
      api.getWorkPlaces(
        (data) => { 
          this.workplaces = data; 
          this.loadingSkeleton = false; 
        },
        (error) => { 
          this.loadingSkeleton = false; 
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 }); 
        }
      );
    },
    getDepartments() {
      this.loadingSkeleton = true;
      const deps = gwt.getDepartments?.();
      if (deps) {
        this.departments = deps;
        this.loadingSkeleton = false;
      } else {
        api.getStaff(
          (data) => { 
            this.departments = data; 
            this.loadingSkeleton = false; 
          },
          (error) => { 
            this.loadingSkeleton = false; 
            this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 }); 
          }
        );
      }
    },
    getProjects() {
      this.loadingSkeleton = true;
      api.getProjects(
        (res) => { 
          this.projects = res; 
          this.loadingSkeleton = false; 
        },
        (error) => { 
          this.loadingSkeleton = false; 
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 }); 
        }
      );
    },
    validate() {
      let isValid = true;
      if (!this.$refs.form1.validate()) { this.tab = 0; isValid = false; }
      if (!this.$refs.form2.validate()) { this.tab = 1; isValid = false; }
      if (this.sessionUserIsSuperAdmin) {
        const form3 = this.$refs.form3;
        if (form3 && !form3.validate()) {
          this.tab = 2;
          isValid = false;
        }
      }
      return isValid;
    },
    hideCompanyDropDown() {
      this.fullEmployees = [];
      this.companyOptions = [];
      this.isCompanySelectorVisible = false;
      this.data.employeeCompanyId = null;
    },
    showCompanyDropDown(employees) {
      this.fullEmployees = employees || [];

      this.companyOptions = [
        { id: '', name: `-- ${this.$t('wkt.admin.common.select_company')} --`|| `-- ${this.$t('wkt.admin.common.select_company')} --`},
        ...employees.map(e => ({ id: e.companyId, name: e.companyName }))
      ];

      // Dejamos el v-model en '' para que empiece como inválido
      this.data.employeeCompanyId = '';

      this.isCompanySelectorVisible = true;
      this.loading = false;

      this.$nextTick(() => {
        this.$refs.form1?.validate();
      });
    },
    existEmployeesByEmail(email, callback) {
      this.employeesFetching = true;
      api.getEmployeesByEmail(
        email,
        (emps) => {
          this.employeesFetching = false;
          if (!emps?.length) {
            this.$emit('show-alert', {
              text: this.$t('wkt.admin.profiles.edit-window.error_no_employee'),
              color: 'error', timeout: 3000
            });
            // mantenemos la UI limpia
            this.hideCompanyDropDown();
            this.employeesFetched = true;
            this.loading = false;
            return;
          }

          // marcamos que ya se ha consultado y guardamos los empleados
          this.employeesFetched = true;
          this.fullEmployees = emps;

          if (emps.length === 1) {
            callback(emps[0]);
          } else {
            this.showCompanyDropDown(emps);
          }

          this.loading = false;
        },
        (err) => {
          this.employeesFetching = false;
          this.loading = false;
          this.$emit('show-alert', { text: err.message, color: 'error', timeout: 3000 });
        }
      );
    },
    existProfile(employee, callback) {
      api.existAdminProfile(
        employee.employeeId,
        gwt.getSessionInformation().company.id,
        (exists) => {
          if (exists) {
            // ya existe -> error y no permitimos guardar
            this.loading = false;
            this.companyValid = false;
            this.$emit('show-alert', {
              text: this.$t('wkt.admin.profiles.edit-window.error_exists_email'),
              color: 'error',
              timeout: 3000
            });
            return;
          }
          // ok, rellenamos campos del formulario
          this.data.employeeId = employee.employeeId;
          this.data.companyId = gwt.getSessionInformation().company.id;
          this.data.employeeCompanyName = employee.companyName;

          // finalizamos loading y permitimos acciones posteriores
          this.loading = false;
          callback();
        },
        (err) => {
          this.loading = false;
          this.companyValid = false;
          this.$emit('show-alert', { text: err.message, color: 'error', timeout: 3000 });
        }
      );
    },
    onBlurEvent() {
      if (this.isEditing) return;

      const email = (this.data.employeeEmail || '').trim();
      if (!email) {
        this.hideCompanyDropDown();
        return;
      }

      if (this.employeesFetched) {
        if (this.fullEmployees.length === 1) {
          this.afterFoundEmployee(this.fullEmployees[0], false);
        } else {
          this.showCompanyDropDown(this.fullEmployees);
        }
        return;
      }

      this.existEmployeesByEmail(email, (emp) => {
        if (emp) {
          this.afterFoundEmployee(emp, false);
        }
      });
    },
    onSave() {
      if (!this.validate()) return;

      if (this.isEditing) {
        return this.doStore();
      }

      // Si ya hay una petición en curso, marcamos que queremos guardar una vez acabe
      if (this.employeesFetching) {
        this.pendingStoreAfter = true;
        return;
      }

      if (!this.employeesFetched) {
        this.existEmployeesByEmail(this.data.employeeEmail, emp => {
          this.afterFoundEmployee(emp, /* storeAfter */ true);
        });
      } else {
        const emp = this.fullEmployees.length === 1
          ? this.fullEmployees[0]
          : this.fullEmployees.find(e => e.companyId === this.data.employeeCompanyId);

        if (!emp) {
          this.companyValid = false;
          return;
        }

        this.afterFoundEmployee(emp, /* storeAfter */ true);
      }
    },
    onCompanySelected(companyId) {
      // buscamos el empleado
      const employee = this.fullEmployees.find(e => e.companyId === companyId);
      if (!employee) {
        this.companyValid = false;
        this.$emit('show-alert', {
          text: this.$t('wkt.admin.common.select_company'),
          color: 'error',
          timeout: 3000
        });
        return;
      }

      this.companyValid = false;

      this.afterFoundEmployee(employee, /* storeAfter */ false);
    },
    afterFoundEmployee(employee, storeAfter = false) {
      const shouldStore = storeAfter || this.pendingStoreAfter;
      // limpiamos la bandera (se consume acá)
      this.pendingStoreAfter = false;

      this.existProfile(employee, () => {
        this.companyValid = true;
        if (shouldStore) {
          this.doStore();
        }
      });
    },
    doStore() {
      const payload = { ...this.data };
      payload.departments = payload.departments.length ? payload.departments.join(',') : null;
      payload.workplaces = payload.workplaces.length  ? payload.workplaces.join(',')  : null;
      payload.projects = payload.projects.length    ? payload.projects.join(',')    : null;
      payload.viewPrivateDocuments == true ? payload.viewPrivateDocuments = 1 : payload.viewPrivateDocuments = 0;

      delete payload.employeeEnabled;
      delete payload.employeeEmail;
      delete payload.employeeCompanyName;
      delete payload.employeeCompanyId;
      delete payload.employeeFirstname;
      delete payload.employeeLastname;
      delete payload.employeeLocale;
      delete payload.employeeProfilePicture;
      delete payload.workplaceId;
      delete payload.workplaceName;
      delete payload.departmentId;
      delete payload.companyName;
      delete payload.departmentName;

      this.loading = true;

      api.storeAdminProfile(
        payload,
        (res) => {
          this.loading = false;
          this.$emit('refresh');
          if (res.rsOK == false) {
            this.$emit('show-alert', { text: res.message, color: 'error', timeout: 3000 });
          }
          this.showDialog = false;
        },
        (err) => {
          this.loading = false;
          this.$emit('show-alert', { text: err.message || err, color: 'error', timeout: 3000 });
        }
      );
    },
    confirmDelete() {
      this.$refs.dlgConfirm.confirm(this.data);
    },
    deleteItem() {      
      this.loadingSkeleton = true;

      api.deleteAdminProfile(this.data.id,
        (res) => {
          this.loadingSkeleton = false;
          this.$emit("refresh");
          if (res.rsOK == false) {
            this.$emit('show-alert', { text: res.message, color: 'error', timeout: 3000 });
          }
          this.showDialog = false;
        },
        (error) => {
          this.loadingSkeleton = false;
          this.$log.error(error);
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    }
  }
};
</script>